/**
 * Anticipi fatture (1.62, analysis of 11/10/2026 on the real movements): the
 * bank lends part of the issued invoices presented to it, up to 100 %, and
 * takes it back when the customer pays. At BCC the customer pays the whole
 * invoice on the account and the bank then debits «Rimborso anticipo … SALDO
 * FATT. N. …»; if the customer does not pay, «Insoluto su anticipo fatture»
 * takes the advance back and the invoice stays open. An advance is a loan,
 * never a collection: the invoices are collected by the customer's payment.
 * At Intesa (1.63, advances until 02/2025) the customer pays the bank: the
 * account gets only what is left over («SALDO FATTURA N. … CON IMPORTO
 * ANTICIPATO …»), or nothing; the part the bank keeps is a collection of the
 * invoice marked without a movement. We may also repay an advance ourselves
 * («… DECURTAZIONE FATTURA»).
 */
import { z } from 'zod';
import { fromCents, toCents } from './money.js';
import { idSchema, isoDateSchema } from './validation.js';
export const ADVANCE_MOVEMENT_LABELS = {
    erogazione: 'Anticipo ricevuto',
    rimborso: 'Rimborso dell’anticipo',
    insoluto: 'Insoluto: anticipo ripreso dalla banca',
    estinzione: 'Anticipo estinto da noi',
    saldo: 'Saldo del cliente, al netto di quanto trattenuto dalla banca',
    interessi: 'Interessi sugli anticipi',
};
function italianCents(text) {
    return toCents(text.replace(/\./g, '').replace(',', '.'));
}
const PRESENTATION = /Anticipo su fatture\s*\*?\s*Presentazione\s+n\.?\s*(\d+)(?:\s+del\s+\d{2}\/\d{2}\/\d{4})?\s+di\s+euro\s+([\d.]+,\d{2})/i;
const FOLLOW_UP = /Anticipo su fatture\s*\*?\s*Ant\.?\s*Succ\.?\s*Distinta\s+n\.?\s*(\d+)/i;
const REPAYMENT = /Rimborso anticipo su effetti\/doc\.?(?:\s*s\.?b\.?f\.?)?\s*\*?\s*(SALDO|ACCONTO)\s+FATT\.?\s*(?:N\.?)?\s*0*(\d+)\s*(.*)$/i;
const UNPAID = /Insoluto su anticipo fatture\s*\*?\s*DOC\.?\s*0*(\d+)\s*(.*)$/i;
/** Intesa: «Pagamento o incasso di documenti su Italia · … Importo Operazione: 63135, …». */
const INTESA_DOCUMENTS = /Pagamento o incasso di documenti/i;
const INTESA_PRESENTED = /Importo Operazione\s*:\s*(\d[\d.]*)(?:,(\d{1,2}))?/i;
const INTESA_SALDO = /SALDO FATTURA N\.?\s*(\S+)\s+DEL\s+(\d{2})\.(\d{2})\.(\d{4})\s+CON IMPORTO ANTICIPATO\s+([\d.]+,\d{2})/i;
const INTESA_SEVERAL = /FATTURE ANTICIPATE PER E\.\s*([\d.]+,\d{2})/i;
/** A movement of the invoice advances, read from the description of the bank, or null. */
export function readAdvanceMovement(movement) {
    const text = movement.description.replace(/\s+/g, ' ').trim();
    const cents = toCents(movement.amount);
    const intesa = readIntesaAdvance(text, cents);
    if (intesa)
        return intesa;
    if (cents > 0) {
        const presented = PRESENTATION.exec(text);
        if (presented) {
            return {
                kind: 'erogazione',
                reference: `Presentazione n. ${presented[1]}`,
                presented: fromCents(italianCents(presented[2])),
                followUp: false,
            };
        }
        const followUp = FOLLOW_UP.exec(text);
        if (followUp) {
            return {
                kind: 'erogazione',
                reference: `Distinta n. ${followUp[1]}`,
                presented: null,
                followUp: true,
            };
        }
        if (/Anticipo su fatture/i.test(text)) {
            return { kind: 'erogazione', reference: null, presented: null, followUp: false };
        }
        return null;
    }
    const repaid = REPAYMENT.exec(text);
    if (repaid) {
        return {
            kind: 'rimborso',
            invoiceNumber: repaid[2],
            part: repaid[1].toUpperCase() === 'SALDO' ? 'saldo' : 'acconto',
            counterparty: repaid[3].trim() || null,
        };
    }
    const unpaid = UNPAID.exec(text);
    if (unpaid) {
        return {
            kind: 'insoluto',
            invoiceNumber: unpaid[1],
            part: 'saldo',
            counterparty: unpaid[2].trim() || null,
        };
    }
    return null;
}
function readIntesaAdvance(text, cents) {
    if (cents < 0 && /COMPETENZE DI CHIUSURA ANTICIPI/i.test(text))
        return { kind: 'interessi' };
    if (INTESA_DOCUMENTS.test(text)) {
        if (cents < 0 && /DECURTAZIONE FATTURA/i.test(text))
            return { kind: 'estinzione' };
        const presented = INTESA_PRESENTED.exec(text);
        if (cents > 0 && presented) {
            const whole = presented[1].replace(/\./g, '');
            const decimals = (presented[2] ?? '').padEnd(2, '0');
            return {
                kind: 'erogazione',
                reference: (() => {
                    const operation = /Riferimento Operazione\s*:\s*([A-Z0-9]+)/i.exec(text)?.[1];
                    return operation ? `Rif. ${operation}` : null;
                })(),
                presented: fromCents(toCents(`${whole}.${decimals}`)),
                followUp: false,
            };
        }
    }
    if (cents > 0) {
        const one = INTESA_SALDO.exec(text);
        if (one) {
            return {
                kind: 'saldo',
                invoiceNumber: one[1],
                invoiceDate: `${one[4]}-${one[3]}-${one[2]}`,
                retained: fromCents(italianCents(one[5])),
            };
        }
        const several = INTESA_SEVERAL.exec(text);
        if (several) {
            return {
                kind: 'saldo',
                invoiceNumber: null,
                invoiceDate: null,
                retained: fromCents(italianCents(several[1])),
            };
        }
    }
    return null;
}
/** The number of an invoice as the bank writes it: the first run of digits, without zeros in front. */
export function invoiceNumberCore(number) {
    const digits = /\d+/.exec(number)?.[0];
    return digits ? digits.replace(/^0+(?=\d)/, '') : null;
}
/** The invoices an advance was given on, each with what was presented. */
export const advanceInvoicesSchema = z.object({
    invoices: z
        .array(z.object({
        invoiceId: idSchema,
        presented: z
            .string()
            .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
            .transform((value) => fromCents(toCents(value)))
            .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    }))
        .min(1, { message: 'Scegli almeno una fattura' })
        .max(50)
        .refine((items) => new Set(items.map((item) => item.invoiceId)).size === items.length, {
        message: 'Una fattura compare due volte',
    }),
});
/**
 * The advance of each invoice: the amount advanced shared in proportion to
 * what was presented, the cents left over on the last one. A bank never
 * advances more than 100 % (20/10/2026): no invoice gets more than what was
 * presented of it, and what the invoices indicated do not cover stays on the
 * advance, for the invoices still to indicate.
 */
export function advanceShares(advanced, presented) {
    const total = presented.reduce((sum, item) => sum + toCents(item), 0);
    const cents = Math.min(toCents(advanced), total);
    if (!total)
        return presented.map(() => '0.00');
    let given = 0;
    return presented.map((item, index) => {
        const share = index === presented.length - 1 ? cents - given : Math.round((cents * toCents(item)) / total);
        given += share;
        return fromCents(Math.min(share, toCents(item)));
    });
}
/** The days after an advance its invoices may be dated: the end of the month presented before it is issued (20/10/2026). */
export const ADVANCE_LATER_INVOICE_DAYS = 31;
/**
 * The invoices of a customer whose amounts add up to what was presented: the
 * smallest group (up to 6 invoices) with that sum, the most recent first.
 */
export function presentedInvoices(candidates, presented, 
/** Invoices the bank later repaid: first among those alike (12/10/2026). */
preferred = new Set()) {
    const target = toCents(presented);
    const pool = [...candidates]
        .filter((item) => toCents(item.amount) > 0 && toCents(item.amount) <= target)
        .sort((a, b) => Number(preferred.has(b.id)) - Number(preferred.has(a.id)) || b.date.localeCompare(a.date))
        .slice(0, 25);
    for (let size = 1; size <= Math.min(6, pool.length); size += 1) {
        const found = combination(pool, size, target);
        if (found)
            return found;
    }
    return null;
}
/**
 * The invoices an advance was given on, read from what the bank says was
 * presented: the amounts to collect first, then the totals with the VAT
 * (18/10/2026: Intesa writes the invoices with the VAT, which in split
 * payment is not collected). Each with the amount to say presented.
 */
export function presentedGroup(candidates, presented, preferred = new Set()) {
    for (const field of ['netDue', 'total']) {
        const found = presentedInvoices(candidates.map((item) => ({ id: item.id, date: item.date, amount: item[field], item })), presented, preferred);
        if (found)
            return found.map((chosen) => ({ item: chosen.item, presented: chosen.amount }));
    }
    return null;
}
/**
 * What a bank would have presented of an invoice, to compare with what is
 * still free in an advance: the amount to collect or the total with the VAT.
 */
export function presentedAmountFor(invoice, free) {
    if (free === null)
        return null;
    const cents = toCents(free);
    if (cents === toCents(invoice.netDue))
        return invoice.netDue;
    if (cents === toCents(invoice.total))
        return invoice.total;
    return null;
}
function combination(pool, size, target, start = 0, chosen = [], sum = 0) {
    if (chosen.length === size)
        return sum === target ? [...chosen] : null;
    for (let index = start; index < pool.length; index += 1) {
        const item = pool[index];
        const next = sum + toCents(item.amount);
        if (next > target)
            continue;
        chosen.push(item);
        const found = combination(pool, size, target, index + 1, chosen, next);
        chosen.pop();
        if (found)
            return found;
    }
    return null;
}
/**
 * Which advance each repayment or unpaid closes: the advance of an invoice
 * with that number, given on or before the day, with something still open,
 * the oldest first. What finds none stays unmatched (the advance not yet
 * linked to its invoices).
 */
export function matchAdvanceClosures(items, closures) {
    const open = new Map(items.map((item) => [`${item.advanceId}|${item.invoiceId}`, toCents(item.advanced)]));
    const ordered = [...items].sort((a, b) => a.advanceDate.localeCompare(b.advanceDate));
    const closed = new Map();
    const unmatched = [];
    const add = (key, closure, cents) => {
        open.set(key, (open.get(key) ?? 0) - cents);
        const list = closed.get(key) ?? [];
        const same = list.find((item) => item.movementId === closure.movementId);
        if (same)
            same.amount = fromCents(toCents(same.amount) + cents);
        else {
            list.push({
                movementId: closure.movementId,
                date: closure.date,
                kind: closure.kind,
                amount: fromCents(cents),
            });
        }
        closed.set(key, list);
    };
    for (const closure of [...closures].sort((a, b) => a.date.localeCompare(b.date))) {
        const candidates = ordered.filter((candidate) => candidate.advanceDate <= closure.date &&
            invoiceNumberCore(candidate.invoiceNumber) === invoiceNumberCore(closure.invoiceNumber) &&
            (open.get(`${candidate.advanceId}|${candidate.invoiceId}`) ?? 0) > 0);
        if (!candidates.length) {
            unmatched.push(closure);
            continue;
        }
        // One repayment closes the first advance of the invoice and then the further ones
        // («Ant. Succ.», 12/10/2026); what is left over stays on the first.
        const [first] = candidates;
        let left = toCents(closure.amount);
        for (const item of candidates.filter((candidate) => candidate.invoiceId === first.invoiceId)) {
            if (left <= 0)
                break;
            const key = `${item.advanceId}|${item.invoiceId}`;
            const taken = Math.min(open.get(key) ?? 0, left);
            add(key, closure, taken);
            left -= taken;
        }
        if (left > 0)
            add(`${first.advanceId}|${first.invoiceId}`, closure, left);
    }
    return { closed, unmatched };
}
export const ADVANCE_STATE_LABELS = {
    'da-collegare': 'Fatture da indicare',
    aperto: 'Aperto',
    'in-parte': 'Rimborsato in parte',
    chiuso: 'Rimborsato',
    insoluto: 'Insoluto',
};
export const ADVANCE_CLOSURE_LABELS = {
    rimborso: 'rimborso',
    insoluto: 'insoluto',
    estinzione: 'estinto da noi',
    trattenuto: 'trattenuto dalla banca',
};
/** An invoice added to an advance from the invoice (2.09): the others stay. */
export const addAdvanceInvoiceSchema = z.object({
    presented: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value)))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
});
/**
 * Repayments that cite no invoice (Intesa, «decurtazione fattura»). First
 * (2.18) the one equal to the cent to what is still owed on an invoice after
 * a collection the bank kept on or before its day: the customer paid less
 * than the bank lent and we paid the rest, so it goes to that invoice and its
 * advance. Then (2.19) the one equal to the cent to all an invoice still owes
 * on its advances, given on its due date or after: we gave the advance back,
 * the customer did not pay the bank; the invoice due closest to that day
 * first. Then (2.20) the one equal to the cent to all an advance still has
 * open. The others go to the advances still open, the oldest first, as far as
 * each is open.
 */
export function allocateExtinctions(advances, extinctions) {
    const open = new Map(advances.map((item) => [item.id, toCents(item.open)]));
    const ordered = [...advances].sort((a, b) => a.date.localeCompare(b.date));
    const result = new Map();
    const give = (id, entry) => result.set(id, [...(result.get(id) ?? []), entry]);
    const sorted = [...extinctions].sort((a, b) => a.date.localeCompare(b.date));
    // The rest of an invoice the bank kept a collection of: matched to the cent.
    const owed = new Map(advances.flatMap((advance) => (advance.items ?? []).map((item) => [`${advance.id}|${item.invoiceId}`, toCents(item.open)])));
    const matched = new Set();
    for (const extinction of sorted) {
        const cents = toCents(extinction.amount);
        const candidates = ordered.flatMap((advance) => advance.date > extinction.date
            ? []
            : (advance.items ?? [])
                .filter((item) => owed.get(`${advance.id}|${item.invoiceId}`) === cents &&
                item.retainedOn.some((day) => day <= extinction.date))
                .map((item) => ({
                advance,
                item,
                kept: item.retainedOn
                    .filter((day) => day <= extinction.date)
                    .sort()
                    .pop(),
            })));
        // The collection kept closest before the repayment.
        const found = candidates.sort((a, b) => b.kept.localeCompare(a.kept))[0];
        if (found && (open.get(found.advance.id) ?? 0) >= cents) {
            owed.set(`${found.advance.id}|${found.item.invoiceId}`, 0);
            open.set(found.advance.id, (open.get(found.advance.id) ?? 0) - cents);
            matched.add(extinction.movementId);
            give(found.advance.id, { ...extinction, invoiceId: found.item.invoiceId });
            continue;
        }
        // All an invoice still owes on its advances, given back by us (2.19): the
        // customer did not pay the bank. The invoice due closest to that day first.
        const byInvoice = new Map();
        for (const advance of ordered) {
            for (const item of advance.items ?? []) {
                const left = owed.get(`${advance.id}|${item.invoiceId}`) ?? 0;
                if (left <= 0)
                    continue;
                const entry = byInvoice.get(item.invoiceId) ?? { parts: [], dueDate: item.dueDate ?? null };
                entry.parts.push({ advance, owed: left });
                byInvoice.set(item.invoiceId, entry);
            }
        }
        const distance = (day) => day === null
            ? Number.MAX_SAFE_INTEGER
            : Math.abs(Date.parse(day) - Date.parse(extinction.date));
        const whole = [...byInvoice.entries()]
            .filter(([, entry]) => entry.parts.reduce((sum, part) => sum + part.owed, 0) === cents &&
            // At its due date or after: the bank recalls the advance the customer did not pay.
            entry.dueDate !== null &&
            entry.dueDate <= extinction.date &&
            entry.parts.every((part) => part.advance.date <= extinction.date && (open.get(part.advance.id) ?? 0) >= part.owed))
            .sort(([, a], [, b]) => distance(a.dueDate) - distance(b.dueDate))[0];
        if (!whole) {
            // All an advance still has open, given back by us (2.20): the part of no
            // invoice in K-Management, or of one not indicated. The oldest such advance.
            const advance = ordered.find((candidate) => candidate.date <= extinction.date &&
                (open.get(candidate.id) ?? 0) === cents &&
                (candidate.items ?? []).every((item) => item.retainedOn.every((day) => day <= extinction.date)));
            if (!advance)
                continue;
            open.set(advance.id, 0);
            matched.add(extinction.movementId);
            give(advance.id, { ...extinction });
            continue;
        }
        const [invoiceId, entry] = whole;
        for (const part of entry.parts) {
            owed.set(`${part.advance.id}|${invoiceId}`, 0);
            open.set(part.advance.id, (open.get(part.advance.id) ?? 0) - part.owed);
            give(part.advance.id, { ...extinction, amount: fromCents(part.owed), invoiceId });
        }
        matched.add(extinction.movementId);
    }
    for (const extinction of sorted) {
        if (matched.has(extinction.movementId))
            continue;
        let left = toCents(extinction.amount);
        for (const advance of ordered) {
            if (left <= 0)
                break;
            const room = open.get(advance.id) ?? 0;
            if (room <= 0 || advance.date > extinction.date)
                continue;
            const taken = Math.min(room, left);
            open.set(advance.id, room - taken);
            left -= taken;
            give(advance.id, {
                movementId: extinction.movementId,
                date: extinction.date,
                amount: fromCents(taken),
            });
        }
    }
    return result;
}
/** Intesa: the part the bank kept for its advance, collected on an invoice by hand (1.63). */
export const bankRetainedSchema = z.object({
    /**
     * The invoice collected, when it is not the one of the advance (2.17): the
     * one issued again after a credit note stornò the invoice of the advance.
     */
    collectedInvoiceId: idSchema.nullish(),
    date: isoDateSchema,
    amount: z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
        .transform((value) => fromCents(toCents(value)))
        .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
});
//# sourceMappingURL=advances.js.map